/**
 * Admin login.
 *
 * Three modes, selected by env:
 *
 *   1. Dev-stub (default, VITE_USE_AUTH=false or unset)
 *      Renders a friendly "dev mode" notice + "Continue as Dev User" button.
 *
 *   2. Clerk (VITE_USE_AUTH=true + VITE_CLERK_PUBLISHABLE_KEY=pk_xxx)
 *      Renders Clerk's <SignIn /> component. After sign-in, Clerk redirects
 *      back to "/" and the app picks up the session automatically.
 *
 *   3. Auth requested but not configured (VITE_USE_AUTH=true, no Clerk key)
 *      Renders a setup-required notice.
 *
 * Clerk's SignIn is loaded lazily so the dev-stub bundle doesn't ship Clerk JS.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { lazy, Suspense, type ReactNode } from "react";

import { Button, Card, CardContent, Spinner } from "@bizop/ui-kit";

const SESSION_KEY = "bizop-session";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const ClerkSignIn = lazy(async () => {
  const mod = await import("@clerk/clerk-react");
  return { default: mod.SignIn };
});

function LoginPage() {
  const navigate = useNavigate();
  const useAuth =
    (import.meta.env.VITE_USE_AUTH ?? "").toString().toLowerCase() === "true";
  const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  // Mode 2: Clerk active.
  if (useAuth && clerkKey) {
    return (
      <div className="min-h-dvh bg-(--bg-canvas) flex items-center justify-center p-4">
        <Suspense
          fallback={
            <div className="flex items-center justify-center p-8">
              <Spinner />
            </div>
          }
        >
          <ClerkSignIn
            path="/login"
            signUpUrl="/login"
            afterSignInUrl="/"
            afterSignUpUrl="/"
            appearance={{
              elements: {
                rootBox: "w-full max-w-sm",
                card: "shadow-none border border-[var(--border-subtle)] rounded-[var(--radius-lg)]",
              },
            }}
          />
        </Suspense>
      </div>
    );
  }

  // Mode 1: dev stub (default).
  if (!useAuth) {
    return (
      <Centered
        title="Sign in to BizOP"
        sub="Auth is not yet configured — running in dev mode."
        body={
          <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3 text-[12px] text-(--text-tertiary)">
            Set <code className="font-mono">VITE_USE_AUTH=true</code> and add a{" "}
            <code className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</code> to your
            root <code className="font-mono">.env.local</code> to enable Clerk
            sign-in. See <code className="font-mono">DEPLOYMENT.md</code> for the
            walkthrough.
          </div>
        }
        cta={
          <Button
            type="button"
            onClick={() => {
              window.localStorage.setItem(
                SESSION_KEY,
                JSON.stringify({
                  token: `dev_${Date.now().toString(36)}`,
                  expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
                  role: "admin",
                  mode: "dev",
                }),
              );
              navigate({ to: "/" });
            }}
            className="w-full"
          >
            Continue as Dev User
          </Button>
        }
      />
    );
  }

  // Mode 3: auth on but no Clerk key configured.
  return (
    <Centered
      title="Auth provider not configured"
      sub="VITE_USE_AUTH=true but no Clerk publishable key was found."
      body={
        <div className="rounded-md border border-(--warning) bg-(--warning-soft) p-3 text-[12px] text-(--text-secondary)">
          Add{" "}
          <code className="font-mono">
            VITE_CLERK_PUBLISHABLE_KEY=pk_test_xxx
          </code>{" "}
          to the root <code className="font-mono">.env.local</code> and restart
          the dev server.
        </div>
      }
      cta={null}
    />
  );
}

function Centered({
  title,
  sub,
  body,
  cta,
}: {
  title: string;
  sub: string;
  body: ReactNode;
  cta: ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-(--bg-canvas) flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="p-6 flex flex-col gap-5">
          <div className="flex flex-col items-center gap-2 text-center">
            <div
              aria-hidden
              className="flex h-12 w-12 items-center justify-center rounded-2xl bg-(--accent-soft) text-(--accent)"
            >
              <Sparkles className="h-6 w-6" />
            </div>
            <h1 className="text-[20px] font-semibold tracking-tight text-(--text-primary)">
              {title}
            </h1>
            <p className="text-[13px] text-(--text-secondary)">{sub}</p>
          </div>
          {body}
          {cta}
        </CardContent>
      </Card>
    </div>
  );
}
